<template>
  <Modal @cancel="close">
    <template v-slot:title>
      <p class="modal-card-title">Add a new flow</p>
    </template>

    <template v-slot:body>
      <div class="field">
        <label for="flow-modal-name-input" class="label">Flow Name</label>
        <div class="control">
          <input
            id="flow-modal-name-input"
            ref="addFlowInput"
            v-model="flowName"
            class="input"
            type="text"
            placeholder="Enter the Flow Name..."
            @input="$v.flowName.$touch()"
            @keypress.enter="addFlow"
            :class="{ 'is-danger': $v.flowName.$invalid && $v.flowName.$dirty }"
          />
          <p class="help is-danger" v-if="$v.flowName.$invalid && $v.flowName.$dirty">{{ getErrorMessage() }}</p>
        </div>
      </div>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button variant="primary" @click="addFlow" :disabled="$v.$invalid" :loading="isLoading">Add</n-button>
        <n-button @click="close" variant="primary--outline">Cancel</n-button>
      </n-button-group>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import Modal from '@/shared/components/Modal.vue';
import { ADD_FLOW } from '@/store/bot-designer/paths/mutationTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';
import { flowsApi } from '@/features/bots/services/flows';
import Toast from '../services/toast';
import appInsights from '../services/appInsights';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import VuelidateUtil from '@/shared/util/vuelidateUtil';
import { useStore } from 'vuex';

const emit = defineEmits<{
  (e: 'flow-added', flow: FlowListItemViewModel): void;
  (e: 'close'): void;
}>();

const store = useStore();

const currentWorkspace = computed(() => store.getters.currentWorkspace);
const addFlowToList = (path: FlowListItemViewModel) => store.commit(ADD_FLOW, path);

const flowName = ref('');
const isLoading = ref(false);
const addFlowInput = ref<HTMLInputElement | null>(null);

// Validation rules for the flowName
const validations = {
  flowName: {
    required,
    maxLength: maxLength(50),
    flowNameValid: VuelidateUtil.objectName,
  },
};

const $v = useVuelidate(validations, { flowName });

onMounted(() => {
  if (addFlowInput.value) {
    addFlowInput.value.focus();
  }
});

async function addFlow() {
  if ($v.value.$invalid) {
    return;
  }

  appInsights.trackEvent({ name: 'Add flow clicked' }, { workspaceId: currentWorkspace.value.id });
  isLoading.value = true;
  try {
    const flowItem = await flowsApi.addFlow(currentWorkspace.value.id, { name: flowName.value });
    addFlowToList(flowItem);
    isLoading.value = false;
    emit('flow-added', flowItem);
    close();
  } catch (e: any) {
    isLoading.value = false;
    if (e.status === 409) {
      Toast.showError(e.message);
      return;
    }
    Toast.showError('Failed to add flow');
  }
}

function getErrorMessage(): string {
  if ($v.value.flowName.required && !$v.value.flowName.flowNameValid) {
    return 'Name must only contain letters, numbers, spaces, and the characters , - _ ( ) &';
  }
  if (!$v.value.flowName.maxLength) {
    return 'Name must not be longer than 50 characters';
  }
  if (!$v.value.flowName.required) {
    return 'Name must not be empty';
  }

  return '';
}

function close() {
  emit('close');
}
</script>
